<template>
  <div v-if="user">
    <div class="page-subtitle mt-5 mb-3">Privacy &amp; Consent</div>

    <p class="text-muted small mb-3">
      Review the documents you have agreed to. Consents cannot be withdrawn here
      - contact
      <a href="mailto:support@tradiepass.com.au">support@tradiepass.com.au</a>
      if you need to.
    </p>

    <b-list-group>
      <b-list-group-item
        v-for="item in consentTypes"
        :key="item.type"
        class="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between"
      >
        <div class="pr-sm-3">
          <div class="font-weight-bold">{{ item.label }}</div>
          <div class="text-muted small">{{ item.description }}</div>
          <div v-if="isAccepted(item)" class="text-success small mt-1">
            <b-icon icon="check-circle-fill" class="mr-1" />
            Accepted<span v-if="acceptedOn(item)">
              on {{ acceptedOn(item) }}</span
            >
          </div>
        </div>

        <div class="mt-2 mt-sm-0 flex-shrink-0">
          <b-badge v-if="isAccepted(item)" variant="success" pill>
            Accepted
          </b-badge>
          <b-button
            v-else
            size="sm"
            variant="primary"
            :disabled="accepting === item.type"
            @click="accept(item)"
          >
            <b-spinner v-if="accepting === item.type" small class="mr-1" />
            Accept
          </b-button>
        </div>
      </b-list-group-item>
    </b-list-group>

    <b-alert
      :show="!!errorMessage"
      variant="danger"
      dismissible
      class="mt-3 mb-0"
      @dismissed="errorMessage = ''"
    >
      {{ errorMessage }}
    </b-alert>

    <div class="small mt-3">
      <a :href="consentUrls.terms" target="_blank" rel="noopener">
        Terms &amp; Conditions
      </a>
      &nbsp;·&nbsp;
      <a :href="consentUrls.privacy" target="_blank" rel="noopener">
        Privacy Policy
      </a>
      &nbsp;·&nbsp;
      <a :href="consentUrls.privacyCollection" target="_blank" rel="noopener">
        Privacy Collection Notice
      </a>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { CONSENT_TYPES, CONSENT_URLS } from '@/constants/consent'

export default {
  name: 'PrivacyConsentOptions',

  data() {
    return {
      // Web has no AI document scanning (all uploads are manual), so the 'ai'
      // consent is omitted here — only consents that map to real web flows show.
      consentTypes: CONSENT_TYPES.filter((c) => c.type !== 'ai'),
      consentUrls: CONSENT_URLS,
      accepting: null,
      errorMessage: '',
    }
  },

  computed: {
    ...mapGetters('auth', ['user']),
  },

  methods: {
    ...mapActions('auth', ['acceptConsent', 'getAuthenticatedUser']),

    isAccepted(item) {
      return !!(this.user && this.user[item.key])
    },

    acceptedOn(item) {
      const raw = this.user && this.user[item.dateKey]
      if (!raw) return ''
      const d = new Date(raw)
      if (Number.isNaN(d.getTime())) return ''
      // Force AU day-first format (dd/mm/yyyy); a bare toLocaleDateString()
      // follows the browser/OS locale and renders US mm/dd/yyyy here.
      return d.toLocaleDateString('en-AU')
    },

    async accept(item) {
      this.accepting = item.type
      this.errorMessage = ''
      try {
        await this.acceptConsent(item.type)
      } catch (e) {
        const payload = (e && e.response && e.response.data) || {}
        this.errorMessage =
          payload.message || 'Could not record your consent. Please try again.'
        this.accepting = null
        return
      }
      // Consent is recorded (acceptConsent merged the response into the store).
      // Re-fetch the full profile too, but a failure here is cosmetic — don't
      // surface it as a consent error.
      try {
        await this.getAuthenticatedUser()
      } catch (e) {
        // ignore — store already has the updated consent flags
      }
      this.accepting = null
    },
  },
}
</script>
